/**
 * <size-guide> — native <dialog> size chart with a cm / in toggle.
 * Opened by any [data-size-guide-open="{section id}"] button (block button or the
 * link next to the size legend, which product-info re-renders, hence document delegation).
 * Conversion: cm × 0.3937, 1 decimal. Preference persisted in localStorage 'hurtliche:units'.
 */
if (!customElements.get('size-guide')) {
  customElements.define(
    'size-guide',
    class SizeGuide extends HTMLElement {
      static STORAGE_KEY = 'hurtliche:units';
      static CM_TO_IN = 0.3937;

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.table = this.querySelector('[data-size-table]');
        this.switch = this.querySelector('[data-unit-switch]');
        this.sourceUnit = (this.table?.dataset.sourceUnit || this.dataset.unit || 'cm').toLowerCase();

        this.querySelector('[data-size-guide-close]')?.addEventListener('click', () => this.close());
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog?.addEventListener('close', () => {
          document.body.classList.remove('overflow-hidden');
          this.opener?.focus?.();
          this.opener = null;
        });
        this.switch?.addEventListener('click', () => this.setUnit(this.unit === 'cm' ? 'in' : 'cm', true));

        this.onDocumentClick = (event) => {
          const trigger = event.target.closest('[data-size-guide-open]');
          if (!trigger) return;
          const target = trigger.dataset.sizeGuideOpen;
          if (target && target !== this.dataset.section) return;
          event.preventDefault();
          this.open(trigger);
        };
        document.addEventListener('click', this.onDocumentClick);

        let stored = null;
        try {
          stored = localStorage.getItem(SizeGuide.STORAGE_KEY);
        } catch (e) {
          /* private mode */
        }
        this.setUnit(stored === 'in' || stored === 'cm' ? stored : this.sourceUnit, false);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      setUnit(unit, persist) {
        this.unit = unit;
        this.switch?.setAttribute('aria-checked', String(unit === 'in'));
        this.querySelectorAll('[data-unit-label]').forEach((label) =>
          label.classList.toggle('is-active', label.dataset.unitLabel === unit)
        );
        this.table?.querySelectorAll('td[data-value]').forEach((cell) => {
          cell.textContent = this.convert(cell.dataset.value, unit);
        });
        if (persist) {
          try {
            localStorage.setItem(SizeGuide.STORAGE_KEY, unit);
          } catch (e) {
            /* private mode */
          }
        }
      }

      convert(raw, unit) {
        if (raw == null || raw === '' || unit === this.sourceUnit) return raw ?? '';
        const factor = unit === 'in' ? SizeGuide.CM_TO_IN : 1 / SizeGuide.CM_TO_IN;
        // converts every number in the cell so ranges like "56-58" work too
        return String(raw).replace(/\d+(?:[.,]\d+)?/g, (match) => {
          const value = parseFloat(match.replace(',', '.')) * factor;
          return String(Math.round(value * 10) / 10);
        });
      }

      open(trigger) {
        if (!this.dialog) return;
        this.opener = trigger || document.activeElement;
        if (typeof this.dialog.showModal === 'function') {
          if (!this.dialog.open) this.dialog.showModal();
          document.body.classList.add('overflow-hidden');
          this.querySelector('[data-size-guide-close]')?.focus();
        } else {
          this.dialog.setAttribute('open', '');
        }
      }

      close() {
        if (!this.dialog) return;
        if (typeof this.dialog.close === 'function') this.dialog.close();
        else this.dialog.removeAttribute('open');
      }
    }
  );
}
